import { getDecayCalibrationTypeName } from '@components/export/spread/decay/decay-change-logs';
import {
  BETA_BLOCK_SIZE,
  DECAY_BLOCK_SIZE,
  populateHeaders,
  SBAL_BLOCK_SIZE,
  START_BETA_COL,
  START_DECAY_COL,
  START_FORECAST_SETTINGS,
  START_HISTORICAL_SETTINGS,
  START_SBAL_COL
} from '@components/settings/calibration-assumptions/calibration-assumptions.spread';
import * as GC from '@grapecity/spread-sheets';
import { IAcquisition } from '@models/acquisition';
import { IBetaCalibration } from '@models/beta/beta-estimate';
import { IMarketRate } from '@models/beta/marketrate';
import { IDecayCalibration } from '@models/decay/decay-calibration';
import { IDecayCalibrationType } from '@models/decay/decay-calibration-type';
import { IDecayEstimate } from '@models/decay/decay-estimate';
import { IExportItem } from '@models/export/export-item';
import { functionalFormsDecay } from '@models/functional-form';
import { applyProductSortingRules, IProduct } from '@models/product';
import { CalibrationFilter, CalibrationStatus } from '@models/record-status';
import { ForecastMethods } from '@models/stable-balance/forecast-settings';
import { PolynomialForms } from '@models/stable-balance/polynomial-forms';
import { IStableBalanceCalibration } from '@models/stable-balance/stable-balance-calibration';
import { getDateString, getLastObservationDateFromCalibration, getMonthYear } from '@shared/dates';
import { getAcquisitionNames } from '@shared/get-acquisition-names';
import { borders, colours, DASH, FONT_NAME, FONT_SIZE } from '@shared/spread/spread-settings';
import { styleRangeBasedOnCalibrationStatus } from '../helpers';

const headerRowsTaken = 3;
const paddingRows = 0;
const colWidth = 20;
const rowHeight = 20;

export const createCalibrationSettingsExport = (
  sheet: GC.Spread.Sheets.Worksheet,
  filteredExportItems: IExportItem[],
  acquisitions: IAcquisition[],
  marketRates: IMarketRate[],
  calibrationFilter: CalibrationFilter,
  calibrationStatus: CalibrationStatus,
  decayCalibrationTypes: IDecayCalibrationType[]
) => {
  try {
    const products = applyProductSortingRules(filteredExportItems.map(({ Product }) => Product));

    setSheetStyling(sheet, products);

    populateHeaders(sheet, GC.Spread.Sheets.SheetArea.viewport);
    populateProductNames(sheet, headerRowsTaken, 0, products);

    populateBeta(sheet, headerRowsTaken, 0, products, filteredExportItems, marketRates, calibrationStatus);
    populateDecay(sheet, headerRowsTaken, 0, products, filteredExportItems, acquisitions, calibrationStatus, decayCalibrationTypes);
    populateStableBalance(sheet, headerRowsTaken, 0, products, filteredExportItems, acquisitions, calibrationFilter, calibrationStatus);
  } catch (err) {
    console.error('Error Exporting Calibration Settings Report', err);
  }
};

function setSheetStyling(sheet: GC.Spread.Sheets.Worksheet, products: IProduct[]) {
  sheet.setRowCount(products.length + paddingRows + headerRowsTaken);
  sheet.setColumnCount(26);

  // Set the row height and column height by default
  setColumnSizes(sheet, { 2: 100, 4: 130, 5: 120 });
  setRowSizes(sheet);

  sheet.frozenColumnCount(1);
  sheet.options.frozenlineColor = 'transparent';

  const headerRange = sheet.getRange(0, 0, headerRowsTaken, sheet.getColumnCount());
  const betaHeaderRange = sheet.getRange(0, START_BETA_COL, 3, BETA_BLOCK_SIZE);
  const stableBalanceHeaderRange = sheet.getRange(0, START_DECAY_COL, 3, DECAY_BLOCK_SIZE);
  const fullRange = sheet.getRange(0, 0, sheet.getRowCount(), sheet.getColumnCount());
  const calibrationsRange = sheet.getRange(0, 1, products.length + headerRowsTaken, sheet.getColumnCount() - 1);
  const productColumn = sheet.getRange(0, 0, products.length + headerRowsTaken, 1);
  const headerRow1st = sheet.getRange(0, 0, 1, sheet.getColumnCount());
  const headerRowLast = sheet.getRange(headerRowsTaken - 1, 0, 1, sheet.getColumnCount());

  fullRange.vAlign(GC.Spread.Sheets.VerticalAlign.center).wordWrap(true);
  fullRange.locked(true);
  fullRange.setBorder(borders.SUBTLE, { all: true });

  calibrationsRange.font(`${parseInt(FONT_SIZE, 10) - 1}pt ${FONT_NAME}`).hAlign(GC.Spread.Sheets.HorizontalAlign.center);
  productColumn.backColor(colours.LIGHT_BLUE);
  productColumn.setBorder(borders.BLACK_MEDIUM, { right: true });
  calibrationsRange.backColor(colours.WHITE);

  // HEADER STYLING
  headerRange
    .backColor(colours.MENU_BG)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
    .font(`${parseInt(FONT_SIZE, 10) - 1}pt ${FONT_NAME}`)
    .wordWrap(true)
    .cellPadding(`0 ${parseInt(FONT_SIZE, 10) / 2}`);

  betaHeaderRange.backColor(colours.MENU_BG2);
  stableBalanceHeaderRange.backColor(colours.MENU_BG2);

  headerRow1st.font(`bold ${parseInt(FONT_SIZE, 10) + 1}pt ${FONT_NAME}`);
  headerRowLast.setBorder(borders.BLACK_MEDIUM, { bottom: true });

  setLeftBorders(sheet, [1, START_BETA_COL, START_DECAY_COL, START_SBAL_COL, START_FORECAST_SETTINGS, START_HISTORICAL_SETTINGS]);
  setBordersBetweenProductTabs(sheet, products);
}

function setColumnSizes(sheet: GC.Spread.Sheets.Worksheet, customColumns?: { [columnNumber: number]: number }) {
  for (let c = 0; c < sheet.getColumnCount(); c++) {
    // First column width
    if (c === 0) {
      sheet.setColumnWidth(0, 200);
    } else {
      if (customColumns && customColumns[c]) {
        sheet.setColumnWidth(c, customColumns[c]);
      } else {
        sheet.setColumnWidth(c, colWidth * 4);
      }
    }
  }
}

function setRowSizes(sheet: GC.Spread.Sheets.Worksheet) {
  for (let r = 0; r < sheet.getRowCount(); r++) {
    // Headers Range row height
    if (r < headerRowsTaken) {
      sheet.setRowHeight(r, 35);
    } else {
      // row height
      sheet.setRowHeight(r, rowHeight);
    }
  }
}

function setLeftBorders(sheet: GC.Spread.Sheets.Worksheet, cols: number[]) {
  cols.forEach((col, i) => {
    sheet.getRange(0, col, sheet.getRowCount() - paddingRows, 1).borderLeft(borders.SUBTLE_DARK);

    // Setting WHITE2 background to odd column ranges
    if (i > 1 && i % 2 !== 0) {
      const ncols = cols[i] - cols[i - 1];
      sheet.getRange(0, col - ncols, sheet.getRowCount() - paddingRows, ncols).backColor(colours.WHITE2);
    }
  });
}

const setBordersBetweenProductTabs = (sheet: GC.Spread.Sheets.Worksheet, products: IProduct[] = []) => {
  for (let i = 1; i < products.length; i++) {
    const product = products[i];
    const productPrev = products[i - 1];
    if (product.TabId !== productPrev.TabId) {
      sheet.getRange(i + headerRowsTaken, 0, 1, sheet.getColumnCount()).borderTop(borders.SUBTLE_DARK);
    }
  }
};

function populateProductNames(sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, colIndex: number, products: IProduct[]): void {
  if (sheet && products && products.length > 0) {
    const productNames = products.map(product => `${product.ProductName}`);
    sheet.setArray(rowIndex, colIndex, productNames);
  }
}

function populateBeta(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  colIndex: number,
  products: IProduct[],
  filteredExportItems: IExportItem[],
  marketRates: IMarketRate[],
  calibrationStatus: CalibrationStatus
) {
  if (sheet && filteredExportItems && filteredExportItems.length > 0) {
    const getMarketRateName = (marketRateId: number): string => {
      return marketRates.find(({ MarketRateId }) => MarketRateId === marketRateId).MarketRateName;
    };
    products.forEach((product, index) => {
      let calibration: IBetaCalibration;
      try {
        const productItems = filteredExportItems.find(({ Product }) => Product.ProductId === product.ProductId);
        if (productItems) {
          const productBeta = productItems.Beta;
          if (productBeta) {
            const productCalibrations = productBeta.ProductCalibrations;

            if (calibrationStatus === CalibrationStatus.Active) {
              calibration = productCalibrations.find(cal => cal.RecordStatus === CalibrationStatus.Active);
            } else {
              calibration = productCalibrations.find(
                cal => cal.RecordStatus === CalibrationStatus.Proposed || cal.RecordStatus === CalibrationStatus.Rejected
              );

              if (!calibration) {
                calibration = productCalibrations.find(cal => cal.RecordStatus === CalibrationStatus.Active);
              }
            }
          }
        }
      } catch (err) {
        console.warn('Unable to find the most recent product BETA calibration for ', product, err);
      }
      let currentColumn = START_BETA_COL;
      if (calibration) {
        sheet.setValue(rowIndex + index, currentColumn++, Number(calibration.BetaCoefficient).toFixed(2));
        sheet.setValue(rowIndex + index, currentColumn++, getMarketRateName(calibration.MarketRateId));
        sheet.setValue(rowIndex + index, currentColumn, calibration.Lag);

        // Styling based on Calibration Status
        styleRangeBasedOnCalibrationStatus(sheet.getRange(rowIndex + index, START_BETA_COL, 1, BETA_BLOCK_SIZE), calibration.RecordStatus);
      }
    });
  }
}

function populateDecay(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  colIndex: number,
  products: IProduct[],
  filteredExportItems: IExportItem[],
  acquisitions: IAcquisition[],
  calibrationStatus: CalibrationStatus,
  decayCalibrationTypes: IDecayCalibrationType[]
) {
  if (sheet && filteredExportItems && filteredExportItems.length > 0) {
    products.forEach((product, index) => {
      const row = rowIndex + index;
      let calibration: IDecayCalibration;
      try {
        const productItems = filteredExportItems.find(({ Product }) => Product.ProductId === product.ProductId);
        if (productItems) {
          const productDecay = productItems.Decay;
          if (productDecay) {
            const productCalibrations = productDecay.ProductCalibrations;

            if (calibrationStatus === CalibrationStatus.Active) {
              calibration = productCalibrations.find(cal => cal.RecordStatus === CalibrationStatus.Active);
            } else {
              calibration = productCalibrations.find(
                cal => cal.RecordStatus === CalibrationStatus.Proposed || cal.RecordStatus === CalibrationStatus.Rejected
              );

              if (!calibration) {
                calibration = productCalibrations.find(cal => cal.RecordStatus === CalibrationStatus.Active);
              }
            }
          }
        }
      } catch (err) {
        console.warn('Unable to find the most recent product DECAY calibration for ', product);
      }

      if (calibration) {
        let estimateModel = null;
        const estimate: IDecayEstimate = calibration.DecayEstimateSetting ? calibration.DecayEstimateSetting.find(e => e.IsChosen) : null;

        if (estimate) {
          const functionalForm = functionalFormsDecay.find(ff => ff.Index === estimate.FunctionalForm);
          estimateModel = functionalForm ? functionalForm.Name : estimate.FunctionalForm;
        }

        let currentColumn = START_DECAY_COL;
        sheet.setValue(row, currentColumn++, getDecayCalibrationTypeName(calibration.DecayCalibrationType, decayCalibrationTypes));
        sheet.setValue(row, currentColumn++, getAcquisitionNames(acquisitions, calibration.ExcludeAcquisitionIds) || DASH);
        sheet.setValue(row, currentColumn++, getMonthYear(calibration.StartDate) || DASH);
        sheet.setValue(row, currentColumn++, getMonthYear(getLastObservationDateFromCalibration(calibration)) || DASH);

        sheet.setValue(row, currentColumn++, estimateModel || DASH);
        sheet.setValue(row, currentColumn++, estimate ? estimate.StartObservation || DASH : DASH);
        sheet.setValue(row, currentColumn++, estimate ? estimate.EndObservation || DASH : DASH);
        sheet.setValue(row, currentColumn, estimate ? estimate.Curtailment || DASH : calibration.ImpliedCurtailment || DASH);

        // Styling based on Calibration Status
        const rowRange = sheet.getRange(row, START_DECAY_COL, 1, DECAY_BLOCK_SIZE);
        styleRangeBasedOnCalibrationStatus(rowRange, calibration.RecordStatus);
      }
    });
  }
}

function populateStableBalance(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  colIndex: number,
  products: IProduct[],
  filteredExportItems: IExportItem[],
  acquisitions: IAcquisition[],
  calibrationFilter: CalibrationFilter,
  calibrationStatus: CalibrationStatus
) {
  if (sheet && filteredExportItems && filteredExportItems.length > 0) {
    products.forEach((product, index) => {
      let calibration: IStableBalanceCalibration;
      try {
        const productItems = filteredExportItems.find(({ Product }) => Product.ProductId === product.ProductId);
        if (productItems) {
          const productStableBalance = productItems.StableBalance;
          if (productStableBalance) {
            const productCalibrations = productStableBalance.ProductCalibrations.filter(
              c =>
                c.RecordStatus === CalibrationStatus.Active ||
                c.RecordStatus === CalibrationStatus.Proposed ||
                c.RecordStatus === CalibrationStatus.Rejected
            );

            if (calibrationStatus === CalibrationStatus.Active) {
              calibration = productCalibrations.find(cal => cal.RecordStatus === CalibrationStatus.Active);
            } else {
              // Proposed Selected
              calibration = productCalibrations.find(
                cal => cal.RecordStatus === CalibrationStatus.Proposed || cal.RecordStatus === CalibrationStatus.Rejected
              );

              if (!calibration) {
                calibration = productCalibrations.find(cal => cal.RecordStatus === CalibrationStatus.Active);
              }
            }
          }
        }
      } catch (err) {
        console.warn('Unable to find the most recent product STABLE BALANCE calibration for ', product);
      }

      if (calibration) {
        const haircut = calibration.Settings.Historical.Haircut;

        let currentColumn = START_SBAL_COL;
        sheet.setValue(rowIndex + index, currentColumn++, getMonthYear(calibration.CalibrationStartDate));
        sheet.setValue(rowIndex + index, currentColumn++, getMonthYear(calibration.CalibrationEndDate));
        sheet.setValue(rowIndex + index, currentColumn++, haircut ? DASH : PolynomialForms[calibration.SelectedPolynomialForm]);
        sheet.setValue(
          rowIndex + index,
          currentColumn++,
          haircut ? DASH : getAcquisitionNames(acquisitions, calibration.SelectedAcquisitionIds) || DASH
        );

        // Historical Settings
        sheet.setValue(rowIndex + index, currentColumn++, haircut ? DASH : calibration.Settings.Historical.StdTerm);
        sheet.setValue(rowIndex + index, currentColumn++, haircut ? DASH : calibration.Settings.Historical.StdMultiplier);
        sheet.setValue(rowIndex + index, currentColumn++, haircut ? DASH : calibration.Settings.Historical.Damping ? 'True' : 'False');

        if (haircut) {
          sheet.setValue(rowIndex + index, currentColumn++, `100 %`);
        } else {
          const nonStablePercent = calibration.NonStablePercents.find(
            b => b.IsHistorical && getDateString(b.PeriodEndDate) === getDateString(calibration.CalibrationEndDate)
          );

          if (nonStablePercent !== null) {
            const endOfTs = 100 * nonStablePercent.Balance;
            sheet.setValue(rowIndex + index, currentColumn++, `${0 + Number(endOfTs.toFixed())} %`);
          } else {
            console.error(`Error: nonStablePercent not found for calibration end date`, {
              calibration
            });
          }
        }

        sheet.setValue(
          rowIndex + index,
          currentColumn++,
          haircut ? DASH : `${(calibration.Settings.Historical.HistoricalUpDamper * 100).toFixed()}%`
        );
        sheet.setValue(
          rowIndex + index,
          currentColumn++,
          haircut ? DASH : `${Math.abs(calibration.Settings.Historical.HistoricalDownDamper * 100).toFixed()}%`
        );

        // @ts-ignore
        const method: ForecastMethods = ForecastMethods[calibration.Settings.Forecast.ForecastMethod];

        // Forecast Settings
        const growthValue = `${(calibration.Settings.Forecast.ForecastGrowth * 100).toFixed()} %`;
        const upDamperValue = `${(calibration.Settings.Forecast.ForecastUpDamper * 100).toFixed()} %`;
        const downDamperValue = `${Math.abs(calibration.Settings.Forecast.ForecastDownDamper * 100).toFixed()} %`;

        sheet.setValue(rowIndex + index, currentColumn++, method);
        sheet.setValue(
          rowIndex + index,
          currentColumn++,
          calibration.Settings.Forecast.ForecastMethod === ForecastMethods['Growth %'] ? growthValue : DASH
        );
        sheet.setValue(
          rowIndex + index,
          currentColumn++,
          calibration.Settings.Forecast.ForecastMethod === ForecastMethods['Haircut %'] ? upDamperValue : DASH
        );
        sheet.setValue(
          rowIndex + index,
          currentColumn,
          calibration.Settings.Forecast.ForecastMethod === ForecastMethods['Haircut %'] ? downDamperValue : DASH
        );

        // Styling based on Calibration Status
        styleRangeBasedOnCalibrationStatus(sheet.getRange(rowIndex + index, START_SBAL_COL, 1, SBAL_BLOCK_SIZE), calibration.RecordStatus);
      }
    });
  }
}
